import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type Device, type Enrolment, type OperatorProfile } from '../lib/api'
import { fmtAgo, fmtFull, fmtMoney, fmtNumber } from '../lib/format'
import { useAuth } from '../lib/auth'
import { Card, Empty, Field, GroupChip, HealthPill, Loading, Modal, SignalBars } from '../components/ui'

export default function Devices() {
  const qc = useQueryClient()
  const { can } = useAuth()
  const [approving, setApproving] = useState<Enrolment | null>(null)

  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: () => api.get<Device[]>('/api/admin/devices'),
    refetchInterval: 5_000,
  })

  const enrolments = useQuery({
    queryKey: ['enrolments'],
    queryFn: () => api.get<Enrolment[]>('/api/admin/enrolments'),
    refetchInterval: 5_000,
  })

  const reject = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/enrolments/${id}/reject`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['enrolments'] })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Devices</h1>
          <div className="page-sub">
            Every ESP32 + SIM800L unit that has asked to join. A device must be approved before it can send.
          </div>
        </div>
      </div>

      {(enrolments.data?.length ?? 0) > 0 && (
        <Card
          title={
            <span className="row" style={{ gap: 8 }}>
              Enrolment queue
              <span className="nav-badge">{enrolments.data!.length}</span>
            </span>
          }
          flush
        >
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Device ID</th>
                  <th>IMEI</th>
                  <th>Firmware</th>
                  <th>From</th>
                  <th>First seen</th>
                  <th className="num">Attempts</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {enrolments.data!.map((e) => (
                  <tr key={e.id}>
                    <td className="mono">{e.deviceId}</td>
                    <td className="mono faint small">{e.imei ?? '—'}</td>
                    <td className="small">{e.firmwareVersion ?? '—'}</td>
                    <td className="mono faint small">{e.remoteIp ?? '—'}</td>
                    <td className="faint small">{fmtAgo(e.firstSeenAt)}</td>
                    <td className="num">{e.attempts}</td>
                    <td>
                      <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                        <button className="btn primary sm" onClick={() => setApproving(e)} disabled={!can('Operator')}>
                          Approve
                        </button>
                        <button
                          className="btn danger sm"
                          onClick={() => reject.mutate(e.id)}
                          disabled={!can('Operator')}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Fleet" flush>
        {devices.isLoading ? (
          <Loading />
        ) : devices.data?.length === 0 ? (
          <Empty
            title="No devices yet"
            hint="Flash a device with the firmware and it will appear here asking for approval."
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Health</th>
                  <th>Signal</th>
                  <th>Groups</th>
                  <th>Balance</th>
                  <th className="num">Lifetime</th>
                  <th>Seen</th>
                </tr>
              </thead>
              <tbody>
                {devices.data?.map((d) => (
                  <tr key={d.id} className="clickable">
                    <td>
                      <Link to={`/devices/${d.id}`}>
                        <strong>{d.name}</strong>
                        <div className="faint small mono">{d.deviceId}</div>
                      </Link>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <HealthPill state={d.healthState} reason={d.darkReason} />
                        {!d.isEnabled && <span className="chip">disabled</span>}
                      </div>
                    </td>
                    <td>
                      <SignalBars rssi={d.rssi} />
                    </td>
                    <td>
                      <div className="row wrap" style={{ gap: 5 }}>
                        {d.groups.length === 0 ? (
                          <span className="faint small">—</span>
                        ) : (
                          d.groups.map((g) => <GroupChip key={g.id} name={g.name} colour={g.colour} />)
                        )}
                      </div>
                    </td>
                    <td className="num">{fmtMoney(d.balanceAmount, d.balanceCurrency)}</td>
                    <td className="num">{fmtNumber(d.dispatchCounter)}</td>
                    <td className="faint small">{d.isConnected ? 'online' : fmtAgo(d.lastHeartbeatAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {approving && (
        <ApproveModal
          enrolment={approving}
          onClose={() => setApproving(null)}
          onDone={() => {
            setApproving(null)
            void qc.invalidateQueries({ queryKey: ['enrolments'] })
            void qc.invalidateQueries({ queryKey: ['devices'] })
            void qc.invalidateQueries({ queryKey: ['dashboard'] })
          }}
        />
      )}
    </div>
  )
}

function ApproveModal({
  enrolment,
  onClose,
  onDone,
}: {
  enrolment: Enrolment
  onClose: () => void
  onDone: () => void
}) {
  const [name, setName] = useState('')
  const [label, setLabel] = useState('')
  const [msisdn, setMsisdn] = useState('')
  const [operatorProfileId, setOperator] = useState('')
  const [error, setError] = useState<string | null>(null)

  const operators = useQuery({
    queryKey: ['operator-profiles'],
    queryFn: () => api.get<OperatorProfile[]>('/api/admin/operator-profiles'),
  })

  const approve = useMutation({
    mutationFn: () =>
      api.post(`/api/admin/enrolments/${enrolment.id}/approve`, {
        name,
        label: label || null,
        msisdn: msisdn || null,
        operatorProfileId: operatorProfileId || null,
      }),
    onSuccess: onDone,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Approval failed.'),
  })

  return (
    <Modal
      title="Approve device"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => approve.mutate()} disabled={!name.trim() || approve.isPending}>
            {approve.isPending ? 'Approving…' : 'Approve'}
          </button>
        </>
      }
    >
      {error && <div className="banner danger">{error}</div>}

      <div className="banner info">
        <div>
          Check the IMEI against the physical unit before approving. Once approved, a token is issued and held for
          10 minutes — the device picks it up on its next connection attempt.
        </div>
      </div>

      <dl className="kv" style={{ marginBottom: 18 }}>
        <dt>Device ID</dt>
        <dd className="mono">{enrolment.deviceId}</dd>
        <dt>IMEI</dt>
        <dd className="mono">{enrolment.imei ?? '—'}</dd>
        <dt>ICCID</dt>
        <dd className="mono">{enrolment.iccid ?? '—'}</dd>
        <dt>Modem</dt>
        <dd>{enrolment.modemModel ?? '—'}</dd>
        <dt>Firmware</dt>
        <dd>{enrolment.firmwareVersion ?? '—'}</dd>
        <dt>First seen</dt>
        <dd>{fmtFull(enrolment.firstSeenAt)}</dd>
      </dl>

      <Field label="Name" hint="How this device is identified everywhere else, e.g. Emtel-01.">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Emtel-01" autoFocus />
      </Field>

      <Field label="Label" hint="Optional. Where the unit physically lives.">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Rack A, shelf 2" />
      </Field>

      <Field label="MSISDN" hint="The SIM's own number. Used for inbound routing and support.">
        <input value={msisdn} onChange={(e) => setMsisdn(e.target.value)} placeholder="+230 5400 0001" />
      </Field>

      <Field label="Operator profile" hint="Drives the USSD balance check. Can be set later.">
        <select value={operatorProfileId} onChange={(e) => setOperator(e.target.value)}>
          <option value="">— none —</option>
          {operators.data?.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
              {o.isVerified ? '' : ' (unverified)'}
            </option>
          ))}
        </select>
      </Field>
    </Modal>
  )
}
